import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download, ExternalLink, Sparkles } from "lucide-react";

const COVER_STYLES = {
    journal: { bg: "linear-gradient(155deg, #3A5F45 0%, #2A4535 100%)", accent: "#C9A961" },
    book: { bg: "linear-gradient(155deg, #5B3B8C 0%, #3B2666 100%)", accent: "#F4DB7D" },
    kids: { bg: "linear-gradient(155deg, #C77687 0%, #A85668 100%)", accent: "#FDFBF7" },
    app: { bg: "linear-gradient(155deg, #1A1817 0%, #2C2A28 100%)", accent: "#3A5F45" },
};

const CLAIM_KEY = "dcl_gift_claim";

const CoverArt = ({ hint, title }) => {
    const s = COVER_STYLES[hint] || COVER_STYLES.book;
    return (
        <div className="aspect-[3/4] relative overflow-hidden" style={{ background: s.bg }}>
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
                <div className="text-4xl mb-4" style={{ color: s.accent }}>†</div>
                <div className="w-16 h-px" style={{ background: s.accent, opacity: 0.7 }} />
                <div className="mt-4 font-serif text-lg leading-tight text-white/95 line-clamp-4">{title}</div>
                <div className="mt-3 small-caps text-[9px] tracking-widest" style={{ color: s.accent, opacity: 0.85 }}>Hearts with Him</div>
            </div>
            <div className="absolute inset-x-4 top-4 h-px" style={{ background: s.accent, opacity: 0.5 }} />
            <div className="absolute inset-x-4 bottom-4 h-px" style={{ background: s.accent, opacity: 0.5 }} />
        </div>
    );
};

const LeadMagnets = () => {
    const [magnets, setMagnets] = useState([]);
    const [first, setFirst] = useState("");
    const [email, setEmail] = useState("");
    const [claim, setClaim] = useState(() => {
        try { return JSON.parse(localStorage.getItem(CLAIM_KEY) || "null"); } catch { return null; }
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => { api.get("/lead-magnets").then(r => setMagnets(r.data)); }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!first || !email) { toast.error("Please share your name and email"); return; }
        setSubmitting(true);
        try {
            const r = await api.post("/lead-magnets/claim", { first_name: first, email });
            const data = { token: r.data.token, email, first_name: first, at: Date.now() };
            localStorage.setItem(CLAIM_KEY, JSON.stringify(data));
            setClaim(data);
            toast.success("Your gifts are unlocked", { description: r.data.message });
        } catch (err) {
            toast.error("Something went awry", { description: err?.response?.data?.detail || "Please try again." });
        } finally { setSubmitting(false); }
    };

    const downloadUrl = (id) => {
        if (!claim) return "#";
        return `${api.defaults.baseURL}/lead-magnets/download/${id}?email=${encodeURIComponent(claim.email)}&token=${encodeURIComponent(claim.token)}`;
    };

    const resetClaim = () => { localStorage.removeItem(CLAIM_KEY); setClaim(null); setFirst(""); setEmail(""); };

    return (
        <div className="relative" data-testid="lead-magnets-page">
            {/* HERO */}
            <div className="relative overflow-hidden border-b border-border">
                <div className="absolute inset-0 -z-0">
                    <img src="https://images.unsplash.com/photo-1519682337058-a94d519337bc?w=1600&q=80" alt="" className="w-full h-full object-cover opacity-15" />
                    <div className="absolute inset-0 bg-background/80" />
                </div>
                <div className="relative max-w-4xl mx-auto px-6 py-20 text-center">
                    <div className="small-caps text-xs text-muted-foreground mb-3">A gift bundle</div>
                    <h1 className="font-serif text-4xl md:text-5xl leading-tight">The My Journey Journal Bundle</h1>
                    <p className="mt-6 max-w-xl mx-auto text-muted-foreground italic leading-relaxed">
                        Five gifts, offered freely — a 30-day journal, an ebook on prayer, a coloring book, a matching children's story, and the free Prayer App as soon as it opens.
                    </p>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-6 md:px-10 py-16">

                {/* CLAIM FORM or SUCCESS STATE */}
                {!claim ? (
                    <form onSubmit={submit} className="missal-card p-8 md:p-10 max-w-2xl mx-auto mb-20 relative overflow-hidden" data-testid="claim-form">
                        <div className="absolute top-0 right-0 w-40 h-40 -mr-16 -mt-16 rounded-full opacity-10 bg-liturgy" />
                        <div className="relative">
                            <div className="small-caps text-[11px] accent-liturgy mb-2 flex items-center gap-2">
                                <Sparkles size={12} /> One signup unlocks all five gifts
                            </div>
                            <h2 className="font-serif text-2xl mb-6">Send them home with me.</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <input value={first} onChange={e => setFirst(e.target.value)} placeholder="First name" data-testid="claim-first"
                                    className="bg-transparent border border-border px-4 py-3 focus:outline-none focus:border-liturgy" />
                                <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="Email" data-testid="claim-email"
                                    className="bg-transparent border border-border px-4 py-3 focus:outline-none focus:border-liturgy" />
                            </div>
                            <button data-testid="claim-submit" disabled={submitting}
                                className="mt-4 w-full py-3 bg-liturgy text-white small-caps disabled:opacity-40">
                                {submitting ? "Preparing your bundle…" : "Unlock the bundle"}
                            </button>
                            <p className="mt-3 text-xs text-muted-foreground text-center">
                                You'll also receive our quiet weekly letter. Unsubscribe anytime.
                            </p>
                        </div>
                    </form>
                ) : (
                    <div className="missal-card p-8 md:p-10 max-w-2xl mx-auto mb-20 text-center border-liturgy" data-testid="claim-success">
                        <div className="small-caps text-[11px] accent-liturgy mb-3 flex items-center justify-center gap-2">
                            <Sparkles size={12} /> Your gifts are ready, {claim.first_name}
                        </div>
                        <h2 className="font-serif text-2xl mb-3">Welcome home.</h2>
                        <p className="text-sm text-muted-foreground leading-relaxed max-w-md mx-auto">
                            A confirmation is on its way to <span className="accent-liturgy">{claim.email}</span>. Below are your downloads — take them at your pace.
                        </p>
                        <button onClick={resetClaim} data-testid="claim-reset" className="mt-5 text-xs small-caps text-muted-foreground hover:accent-liturgy">Use a different email</button>
                    </div>
                )}

                {/* BENTO BUNDLE */}
                <div className="divider-ornament small-caps text-xs mb-10">Inside the bundle</div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-6" data-testid="bundle-grid">
                    {magnets.map((m, idx) => {
                        const isCornerstone = m.id === "prayer-journey-journal";
                        return (
                            <div key={m.id} data-testid={`magnet-${m.id}`}
                                className={`missal-card overflow-hidden hover-lift ${isCornerstone ? 'md:col-span-8' : 'md:col-span-4'}`}>
                                <div className={`grid grid-cols-1 ${isCornerstone ? 'sm:grid-cols-2' : ''}`}>
                                    <CoverArt hint={m.cover_hint} title={m.title} />
                                    <div className="p-6 md:p-8 flex flex-col justify-between">
                                        <div>
                                            {isCornerstone && <div className="small-caps text-[10px] accent-liturgy mb-2">The Cornerstone</div>}
                                            <h3 className="font-serif text-xl md:text-2xl leading-tight">{m.title}</h3>
                                            <p className="mt-2 text-sm text-muted-foreground italic leading-relaxed">{m.subtitle}</p>
                                        </div>
                                        <div className="mt-6 space-y-3">
                                            {m.kind === "pdf" ? (
                                                claim ? (
                                                    <a href={downloadUrl(m.id)} download data-testid={`download-${m.id}`}
                                                        className="inline-flex items-center gap-2 px-4 py-2 bg-liturgy text-white text-xs small-caps hover:opacity-90 transition-opacity">
                                                        <Download size={13} /> Download PDF
                                                    </a>
                                                ) : (
                                                    <div className="inline-flex items-center gap-2 px-4 py-2 border border-border text-xs small-caps text-muted-foreground">
                                                        Sign up above to unlock
                                                    </div>
                                                )
                                            ) : (
                                                <div className="inline-flex items-center gap-2 px-4 py-2 border border-border text-xs small-caps text-muted-foreground" data-testid={`app-${m.id}`}>
                                                    Coming soon — you'll be the first to know
                                                </div>
                                            )}
                                            {m.physical && (
                                                <a href="#" onClick={e => { e.preventDefault(); toast("Coming to the shop soon", { description: m.physical.note }); }}
                                                    data-testid={`physical-${m.id}`}
                                                    className="flex items-center gap-2 text-xs text-muted-foreground hover:accent-liturgy transition-colors">
                                                    <ExternalLink size={11} /> Prefer the physical copy? Available at cost soon.
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <p className="mt-14 text-center text-xs text-muted-foreground max-w-lg mx-auto italic">
                    These are placeholder editions — beautifully typeset stand-ins. When the finished versions arrive from the studio, they will replace these downloads automatically for everyone who has signed up.
                </p>
            </div>
        </div>
    );
};

export default LeadMagnets;
